import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  AppAsideModule,
  AppBreadcrumbModule,
  AppFooterModule,
  AppHeaderModule,
  AppSidebarModule,
} from '@coreui/angular';
import { IconModule, IconSetModule } from '@coreui/icons-angular';
import { NgOptionHighlightModule } from '@ng-select/ng-option-highlight';
import { NgSelectModule } from '@ng-select/ng-select';
import { ChartsModule } from 'ng2-charts';
import { PosUserAndSellerListModalService } from './@core/services/pos-user-and-seller-list-modal.service';
// Import 3rd party components
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import {
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule,
} from 'ngx-perfect-scrollbar';
import { ToastrModule } from 'ngx-toastr';
import { HasPrivilegeModule } from './@core/directives/has-privilege/has-privilege.module';
import { AuthGuard } from './@core/guards/auth.guard';
import { AuthInterceptor } from './@core/interceptor/auth.interceptor';
import { ErrorInterceptor } from './@core/interceptor/error.interceptor';
import { HttpHeadersInterceptor } from './@core/interceptor/httpHeaders.interceptor';
import { Spinnernterceptor as SpinnerInterceptor } from './@core/interceptor/spinner.interceptor';
import { PosSupervisorLoginService } from './@core/services/pos-supervisor-login.service';
// Import routing module
import { HasAccessModule } from './@core/directives/has-access/has-access.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutComponent } from './containers/layout/layout.component';
import { AuthService } from './services/auth.service';
import { ExchangeRateService } from './services/exchange-rate.service';
import { LocalStorageService } from './services/local-storage.service';
import { InvoiceWithoutPaymentModule } from './shared/invoice-without-payment/invoice-without-payment.module';
import { OrderWithPendingInvoiceModule } from './shared/order-with-pending-invoice/order-with-pending-invoice.module';
import { PaymentMethodsMovementModalModule } from './shared/payment-methods-movement-modal/payment-methods-movement-modal.module';
import { PosCaptchaModule } from './shared/pos-captcha/pos-captcha.module';
import { PosChangePriceModalModule } from './shared/pos-change-price-modal/pos-change-price-modal.module';
import { PosConfirmationModule } from './shared/pos-confirmation/pos-confirmation.module';
import { PosCustomerCreateModule } from './shared/pos-customer-create/pos-customer-create.module';
import { PosCustomerTableModule } from './shared/pos-customer-table/pos-customer-table.module';
import { PosCustomerUpdateModalModule } from './shared/pos-customer-update-modal/pos-customer-update-modal.module';
import { PosCustomerUpdateModule } from './shared/pos-customer-update/pos-customer-update.module';
import { PosEmployeeTableModule } from './shared/pos-employee-table/pos-employee-table.module';
import { PosFloatingActionButtonModule } from './shared/pos-floating-action-button/pos-floating-action-button.module';
import { PosListModule } from './shared/pos-list/pos-list.module';
import { PosOrderRejectModalModule } from './shared/pos-order-reject-modal/pos-order-reject-modal.module';
import { PosReasonSelectModalModule } from './shared/pos-reason-select-modal/pos-reason-select-modal.module';
import { PosSpinnerModule } from './shared/pos-spinner/pos-spinner.module';
import { PosStockValidateModalModule } from './shared/pos-stock-validate-modal/pos-stock-validate-modal.module';
import { PosUserAndSellerListModalComponent } from './shared/pos-user-and-seller-list-modal/pos-user-and-seller-list-modal.component';
import { PosVerifyCustomerModule } from './shared/pos-verify-customer/pos-verify-customer.module';
import { AutoLoginModule } from './views/auto-login/auto-login.module';
import { BiometryModule } from './views/biometry/biometry.module';
import { BiometryFingerPrintModule } from './views/biometry/biometryFingerPrint/biometryFingerPrint.module';
import { ChangeForgetPasswordModule } from './views/change-forget-password/change-forget-password.module';
import { ChangePasswordModule } from './views/change-password/change-password.module';
import { ConfigurationsModule } from './views/configurations/configurations.module';
import { CreditRequestModule } from './views/credit-request/credit-request.module';
import { DeliveryTicketModule } from './views/delivery-ticket/delivery-ticket.module';
import { GiftcardsModule } from './views/giftcards/giftcards.module';
import { LoginComponent } from './views/login/login.component';
import { LoginModule } from './views/login/login.module';
import { LogoutComponent } from './views/logout/logout.component';
import { LogoutModule } from './views/logout/logout.module';
import { ProductDetailsModalModule } from './views/products/product-details-modal/product-details-modal.module';
import { ProductsModule } from './views/products/products.module';
import { ReplicateInformationModule } from './views/replicate-information/replicate-information.module';
import { ReturnInvoicesModule } from './views/return-invoices/return-invoices.module';
import { VoidPaymentModule } from './views/void-payment/void-payment.module';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};

const APP_CONTAINERS = [LayoutComponent, PosUserAndSellerListModalComponent];

const maskConfig: Partial<IConfig> = {
  validation: false,
};
@NgModule({
  declarations: [
    AppComponent,
    ...APP_CONTAINERS,
    LoginComponent,
    LogoutComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    AppAsideModule,
    AppBreadcrumbModule.forRoot(),
    AppFooterModule,
    AppHeaderModule,
    AppSidebarModule,
    PerfectScrollbarModule,
    BsDropdownModule.forRoot(),
    TabsModule.forRoot(),
    ChartsModule,
    IconModule,
    IconSetModule.forRoot(),
    HttpClientModule,
    LoginModule,
    NgSelectModule,
    NgOptionHighlightModule,
    TooltipModule.forRoot(),
    ToastrModule.forRoot({ autoDismiss: false, timeOut: 5000 }),
    LogoutModule,
    PopoverModule.forRoot(),
    PosConfirmationModule,
    PosOrderRejectModalModule,
    PosStockValidateModalModule,
    HasPrivilegeModule,
    ProductsModule,
    PosCustomerTableModule,
    PosEmployeeTableModule,
    PosCustomerCreateModule,
    PosCustomerUpdateModule,
    NgxMaskModule.forRoot(maskConfig),
    HasPrivilegeModule,
    ConfigurationsModule,
    ReactiveFormsModule,
    HasAccessModule,
    ChangePasswordModule,
    BiometryModule,
    BiometryFingerPrintModule,
    PosListModule,
    AutoLoginModule,
    PosFloatingActionButtonModule,
    VoidPaymentModule,
    DeliveryTicketModule,
    CreditRequestModule,
    ReturnInvoicesModule,
    PosChangePriceModalModule,
    ChangeForgetPasswordModule,
    ProductDetailsModalModule,
    PosCaptchaModule,
    ReplicateInformationModule,
    PosVerifyCustomerModule,
    PosCustomerUpdateModalModule,
    GiftcardsModule,
    PaymentMethodsMovementModalModule,
    PosReasonSelectModalModule,
    InvoiceWithoutPaymentModule,
    OrderWithPendingInvoiceModule,
    PosReasonSelectModalModule,
    PosSpinnerModule,

  ],
  providers: [
    LocalStorageService,
    // NgxSpinnerService,
    // { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpHeadersInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: SpinnerInterceptor, multi: true },
    AuthGuard,
    AuthService,
    DatePipe,
    ExchangeRateService,
    PosSupervisorLoginService,
    DecimalPipe,
    CurrencyPipe,
    PosUserAndSellerListModalService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
